import {
  GROUP_CHILD_FIELD_TYPE_OPTIONS,
  FieldTypes,
  type FieldDefinitionRequest,
  type FieldType,
} from '../api';
import type { FieldDefinitionDraft } from '../hooks/useFieldDefinitionState';

interface GroupChildrenEditorProps {
  parentDraftId: string;
  children: ReadonlyArray<FieldDefinitionDraft>;
  onAdd: (parentDraftId: string) => void;
  onRemove: (parentDraftId: string, childDraftId: string) => void;
  onUpdate: (
    parentDraftId: string,
    childDraftId: string,
    patch: Partial<Omit<FieldDefinitionRequest, 'children'>>,
  ) => void;
  disabled?: boolean;
}

/**
 * Compact editor for the typed children of a Group field. One row per child with name + type +
 * required + critical + optional regex. Renders inside the parent FieldRow so the user can see the
 * group's internal shape without navigating away.
 *
 * V1 cap: children cannot themselves be Groups (the type selector excludes the Group option), and
 * the row layout is intentionally flatter than a top-level FieldRow — no drag handle, no nested
 * pattern editor for Tables (that lives on the per-child Table editor when V2 lands).
 */
export function GroupChildrenEditor({
  parentDraftId,
  children,
  onAdd,
  onRemove,
  onUpdate,
  disabled = false,
}: GroupChildrenEditorProps): JSX.Element {
  return (
    <div className="rounded-lg border border-indigo-100 bg-indigo-50/30 p-3">
      <div className="mb-2 flex items-center justify-between">
        <h4 className="text-sm font-semibold text-indigo-900">Sub-campos del grupo</h4>
        <span className="text-xs text-slate-500">{children.length} sub-campos</span>
      </div>

      {children.length === 0 ? (
        <div className="rounded border border-dashed border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          Un grupo necesita al menos un sub-campo. Añade el primero para empezar.
        </div>
      ) : (
        <div className="space-y-2">
          {children.map((child, index) => (
            <div
              key={child.draftId}
              className="rounded border border-slate-200 bg-white p-2 shadow-sm"
            >
              <div className="grid grid-cols-1 gap-2 md:grid-cols-12">
                <div className="md:col-span-4">
                  <label
                    htmlFor={`child-name-${child.draftId}`}
                    className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500"
                  >
                    Nombre
                  </label>
                  <input
                    id={`child-name-${child.draftId}`}
                    type="text"
                    className="mt-0.5 w-full rounded border border-slate-300 bg-white px-2 py-1 font-mono text-xs"
                    value={child.name}
                    placeholder="snake_case"
                    onChange={(e) =>
                      onUpdate(parentDraftId, child.draftId, { name: e.target.value })
                    }
                    disabled={disabled}
                  />
                </div>

                <div className="md:col-span-3">
                  <label
                    htmlFor={`child-type-${child.draftId}`}
                    className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500"
                  >
                    Tipo
                  </label>
                  <select
                    id={`child-type-${child.draftId}`}
                    className="mt-0.5 w-full rounded border border-slate-300 bg-white px-2 py-1 text-xs"
                    value={child.type}
                    onChange={(e) => {
                      const next = e.target.value as FieldType;
                      const isTable = next === FieldTypes.Table;
                      const wasTable = child.type === FieldTypes.Table;
                      const patch: Partial<Omit<FieldDefinitionRequest, 'children'>> = {
                        type: next,
                      };
                      if (isTable && !wasTable) {
                        patch.columns = [];
                        patch.pattern = null;
                      } else if (!isTable && wasTable) {
                        patch.columns = null;
                      }
                      onUpdate(parentDraftId, child.draftId, patch);
                    }}
                    disabled={disabled}
                  >
                    {GROUP_CHILD_FIELD_TYPE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="md:col-span-3 flex items-end gap-3 text-xs text-slate-700">
                  <label className="flex items-center gap-1">
                    <input
                      type="checkbox"
                      checked={child.required}
                      onChange={(e) =>
                        onUpdate(parentDraftId, child.draftId, { required: e.target.checked })
                      }
                      disabled={disabled}
                    />
                    Obligatorio
                  </label>
                  <label className="flex items-center gap-1">
                    <input
                      type="checkbox"
                      checked={child.critical}
                      onChange={(e) =>
                        onUpdate(parentDraftId, child.draftId, { critical: e.target.checked })
                      }
                      disabled={disabled}
                    />
                    Crítico
                  </label>
                </div>

                <div className="md:col-span-2 flex items-end justify-end">
                  <button
                    type="button"
                    onClick={() => onRemove(parentDraftId, child.draftId)}
                    className="rounded p-1 text-slate-400 hover:bg-rose-50 hover:text-danger"
                    aria-label={`Eliminar sub-campo ${child.name === '' ? `#${index + 1}` : child.name}`}
                    disabled={disabled}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4 w-4">
                      <path d="M3 6h18M8 6V4h8v2m-1 0v14H9V6" />
                    </svg>
                  </button>
                </div>

                {child.type !== FieldTypes.Table ? (
                  <div className="md:col-span-12">
                    <label
                      htmlFor={`child-pattern-${child.draftId}`}
                      className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500"
                    >
                      Regex (opcional)
                    </label>
                    <input
                      id={`child-pattern-${child.draftId}`}
                      type="text"
                      className="mt-0.5 w-full rounded border border-slate-300 bg-white px-2 py-1 font-mono text-xs"
                      value={child.pattern ?? ''}
                      placeholder="opcional"
                      onChange={(e) =>
                        onUpdate(parentDraftId, child.draftId, {
                          pattern: e.target.value === '' ? null : e.target.value,
                        })
                      }
                      disabled={disabled}
                    />
                  </div>
                ) : null}

                {/*
                  Hint editor per sub-field. Mirrors the textarea on the parent FieldRow so the
                  prompt builder's `## Hints` block can carry per-child guidance. Discovery seeds
                  this slot with the model's suggestion_reason + aliases + nearby label (composed
                  by CandidateField.ToFieldDefinition); the operator can refine it manually.
                */}
                <div className="md:col-span-12">
                  <label
                    htmlFor={`child-hint-${child.draftId}`}
                    className="flex items-center justify-between text-[10px] font-semibold uppercase tracking-wider text-slate-500"
                  >
                    Pistas para la IA (opcional)
                    <span className="text-[10px] font-normal normal-case text-slate-400">
                      {(child.hint ?? '').length}/500
                    </span>
                  </label>
                  <textarea
                    id={`child-hint-${child.draftId}`}
                    rows={2}
                    maxLength={1000}
                    className="mt-0.5 w-full rounded border border-slate-300 bg-white px-2 py-1 text-xs"
                    value={child.hint ?? ''}
                    placeholder="Ej.: aparece en la primera línea del bloque, justo debajo de la razón social."
                    onChange={(e) =>
                      onUpdate(parentDraftId, child.draftId, {
                        hint: e.target.value === '' ? null : e.target.value,
                      })
                    }
                    disabled={disabled}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <button
        type="button"
        onClick={() => onAdd(parentDraftId)}
        className="btn-secondary mt-2 inline-flex items-center gap-1 text-xs"
        disabled={disabled}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-3.5 w-3.5">
          <path d="M12 5v14M5 12h14" />
        </svg>
        Añadir sub-campo
      </button>
    </div>
  );
}
